import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/shared/hooks/use-toast";

import { cn } from "@/shared/lib/utils";
import {
  aiKefuService,
  type FeedbackSignal,
  type FeedbackSubmitBody,
  type FeedbackSummary,
  type SourceChunk,
} from "../../api/aiKefuService";

const emptySummary: FeedbackSummary = { overall: null, perSourceByDocId: {} };

const sourceKey = (docId: string, chunkIndex?: number | null): string =>
  `${docId}#${chunkIndex == null ? "d" : chunkIndex}`;

async function postFeedback(
  sessionId: string,
  messageId: string,
  body: FeedbackSubmitBody,
  onUpdated: (next: FeedbackSummary) => void,
  setPending: (v: boolean) => void,
  msgs: { saved: string; failed: string }
) {
  setPending(true);
  try {
    const next = await aiKefuService.submitFeedback(sessionId, messageId, body);
    onUpdated(next);
    toast({ title: msgs.saved });
  } catch {
    toast({ title: msgs.failed, variant: "destructive" });
  } finally {
    setPending(false);
  }
}

type OverallProps = {
  sessionId: string;
  messageId: string;
  summary: FeedbackSummary | null;
  onUpdated: (next: FeedbackSummary) => void;
};

/**
 * 整条质量打标。inline 紧凑两按钮,放在消息工具栏里——视觉权重稳定,
 * 不论命中片段是否展开,工具栏始终保持一行高度。
 */
export function FeedbackOverall({ sessionId, messageId, summary, onUpdated }: OverallProps) {
  const { t } = useTranslation("customer-service");
  const current = summary ?? emptySummary;
  const [pending, setPending] = useState(false);

  function submit(body: FeedbackSubmitBody) {
    return postFeedback(sessionId, messageId, body, onUpdated, setPending, {
      saved: t("feedback.toastSaved"),
      failed: t("feedback.toastFailed"),
    });
  }

  const isOverall = (s: FeedbackSignal) => current.overall === s;

  return (
    <div data-testid="feedback-overall" className="inline-flex items-center gap-1">
      <PillButton
        icon={<ThumbsUp className="h-3 w-3" />}
        label={t("feedback.good")}
        selected={isOverall("up")}
        disabled={pending}
        onClick={() => submit({ overall: "up" })}
      />
      <PillButton
        icon={<ThumbsDown className="h-3 w-3" />}
        label={t("feedback.bad")}
        selected={isOverall("down")}
        disabled={pending}
        variant="negative"
        onClick={() => submit({ overall: "down" })}
      />
    </div>
  );
}

type SourceProps = {
  sessionId: string;
  messageId: string;
  docId: string;
  chunkIndex?: number | null;
  summary: FeedbackSummary | null;
  onUpdated: (next: FeedbackSummary) => void;
};

/**
 * 单条命中片段的"帮上忙/帮倒忙"按钮。inline 在片段卡片右上角,
 * 操作目标和被评判的内容紧挨,不再像旧版那样把 source list 重复挂在工具栏里。
 */
export function SourceFeedbackButtons({
  sessionId,
  messageId,
  docId,
  chunkIndex,
  summary,
  onUpdated,
}: SourceProps) {
  const { t } = useTranslation("customer-service");
  const current = summary ?? emptySummary;
  const [pending, setPending] = useState(false);
  const key = sourceKey(docId, chunkIndex);
  const signal = current.perSourceByDocId[key] ?? null;
  const hasChunk = chunkIndex != null;

  function submit(s: FeedbackSignal) {
    return postFeedback(
      sessionId,
      messageId,
      {
        perSource: [
          {
            docId,
            ...(hasChunk ? { chunkIndex: chunkIndex! } : {}),
            signal: s,
          },
        ],
      },
      onUpdated,
      setPending,
      { saved: t("feedback.toastSaved"), failed: t("feedback.toastFailed") }
    );
  }

  return (
    <div className="inline-flex items-center gap-0.5 shrink-0" data-testid="feedback-source">
      <IconToggle
        title={t("feedback.sourceUp")}
        selected={signal === "up"}
        disabled={pending}
        onClick={() => submit("up")}
      >
        <ThumbsUp className="h-3 w-3" />
      </IconToggle>
      <IconToggle
        title={t("feedback.sourceDown")}
        variant="negative"
        selected={signal === "down"}
        disabled={pending}
        onClick={() => submit("down")}
      >
        <ThumbsDown className="h-3 w-3" />
      </IconToggle>
    </div>
  );
}

type PillProps = {
  icon: React.ReactNode;
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  variant?: "positive" | "negative";
};

function PillButton({ icon, label, selected, disabled, onClick, variant = "positive" }: PillProps) {
  return (
    <button
      type="button"
      data-selected={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 px-2 h-6 rounded-full border text-2xs leading-none transition-colors",
        selected
          ? variant === "negative"
            ? "bg-destructive/10 text-destructive border-destructive/30"
            : "bg-primary/10 text-primary border-primary/30"
          : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

type IconToggleProps = {
  title: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  variant?: "positive" | "negative";
  children: React.ReactNode;
};

function IconToggle({
  title,
  selected,
  disabled,
  onClick,
  variant = "positive",
  children,
}: IconToggleProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-selected={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center h-6 w-6 rounded-md border transition-colors",
        selected
          ? variant === "negative"
            ? "bg-destructive/10 text-destructive border-destructive/30"
            : "bg-primary/10 text-primary border-primary/30"
          : "bg-background text-muted-foreground/70 border-transparent hover:border-border hover:text-foreground hover:bg-muted",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {children}
    </button>
  );
}

// Re-export SourceChunk for convenience if callers want to build sources from structured chunks
export type { SourceChunk };
